import GoBackMenu from '@/components/GoBackNavbar';
import HorizontalMenu from '@/components/HorizontalMenu';
import LMSContentCard from '@/components/LMSContentCard';
import Loader from '@/components/Loader';
import ReadMore from '@/components/ReadMore';
import SideMenu from '@/components/SideMenu';
import StudentRating from '@/components/StudentRating';
import WorkshopSlider from '@/components/WorkshopSlider';
import Constants from '@/Constants';
import { useCertificate } from '@/hooks/certificate';
import { CourseRequest } from '@/models/CourseResquest';
import { EntityType } from '@/models/EntityTypes';
import { LanguageType } from '@/models/LanguageType';
import { Lesson } from '@/models/Lesson';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import { StartEnrollmentRequest } from '@/models/StartEnrollmentRequest';
import { Workshop } from '@/models/Workshop';
import { LMSEvents } from '@/services/AnalyticsService';
import { courseService } from '@/services/CourseService';
import { getAuth } from '@/services/IdentityService';
import { lmsCourseService } from '@/services/LMSCourseService';
import { formatDate } from '@/services/UtilService';
import { getWorkshopByCourseId } from '@/services/WorkshopService';
import { ApplicationState } from '@/store';
import { getFavoriteListRequest } from '@/store/favorite/actions';
import { clearCourse, getCourseStudentViewRequest, increaseCourseViewsRequest, startEnrollmentRequest } from '@/store/list/action';
import { getProgramClassRequest } from '@/store/programClass/actions';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { DownloadCertificate } from './DownloadCertificate';
import './style.scss';

const LMSCoursePage: React.FC = () => {
    const courseId = parseInt(useParams<{ courseId: string }>().courseId);
    const enrollmentId = parseInt(useParams<{ enrollmentId: string }>().enrollmentId);
    const history = useHistory();
    const dispatch = useDispatch();

    const { courseStudentView, course, courseEnrollment, classEnrollment, person, loading, currentProgram, strings } = useSelector(
        (state: ApplicationState) => ({
            courseStudentView: state.lists.currentData,
            course: state.lists.currentData.course,
            courseEnrollment: state.lists.currentData.courseEnrollment,
            classEnrollment: state.lists.currentData.classEnrollment,
            currentProgram: state.programClass.currentData,
            person: state.identity.data.person,
            loading: state.lists.loading,
            strings: state.locale.data,
        }),
    );

    const courseCertificateMessage = course?.type === 'WORKSHOP' ? strings.WORKSHOP_WITH_CERTIFICATE : strings.WITH_CERTIFICATE;
    const { loadingCertificate, downloadCertificateFile } = useCertificate();
    const [endDate, setEndDate] = useState<string>('');
    const [programClassId, setProgramClassId] = useState<number>(0);
    const [itemsMargin, setItemsMargin] = useState<string>('4rem');
    const [canItShowCertificate, setCanItShowCertificate] = useState<boolean>(false);
    const [canShowContentLink, setCanShowContentLink] = useState<boolean>(false);

    const getClassNameByStatus = (status: string, prerequisitesMet: boolean) => {
        if (!canShowContentLink) {
            return 'hide';
        }

        if (status !== Constants.courseEnrollmentStatus.BLOCKED && prerequisitesMet) {
            return 'link-to-course';
        }

        return 'disable-link';
    };

    const linkRouteLesson = (lesson: Lesson): string => {
        const resourceName: string = lesson.type === 'EvaluationLesson' ? Constants.EVALUATIONS_RESOURCE_NAME : Constants.LESSONS_RESOURCE_NAME;
        return `/lms/enrollments/${classEnrollment.id}/courses/${course.id}/${resourceName}/${lesson.id}`;
    };

    const onClickDownloadCertificate = () => {
        const auth = (getAuth() || '').replace('bearer ', '');
        const url = lmsCourseService.certificateUrl(person, courseEnrollment, auth);
        const filename = `${person.firstName}  ${person.lastName || ''} - ${courseEnrollment.course.name}  - ${strings.CERTIFICATE} .pdf`;

        downloadCertificateFile(url, filename, 'application/pdf');
    };

    useEffect(() => {
        if (
            courseStudentView.programClass?.isExpired &&
            !lmsCourseService.isCourseCompletedPassedOrFailed(courseStudentView.courseEnrollment?.status)
        ) {
            history.push('/');
        }
    }, [courseStudentView.programClass?.isExpired, courseStudentView.courseEnrollment?.status]);

    useEffect(() => {
        if (classEnrollment) {
            setProgramClassId(classEnrollment.programClass.id);
            if (classEnrollment.programClass.finishDate) {
                setEndDate(formatDate(classEnrollment.programClass.finishDate, strings._LANGUAGE as LanguageType));
            }
        }
    }, [classEnrollment]);

    useEffect(() => {
        programClassId && dispatch(getProgramClassRequest(programClassId, false));
    }, [programClassId]);

    useEffect(() => {
        setItemsMargin(canItShowCertificate ? '1.5rem' : '4rem');
    }, [canItShowCertificate]);

    useEffect(() => {
        if (courseEnrollment && currentProgram) {
            const { course, lessonEnrollments } = courseEnrollment;
            let shouldShowCertificate: boolean = lmsCourseService.canItShowCertificate(
                !!currentProgram.hasCourseCertification,
                lessonEnrollments,
                courseEnrollment.status,
            );

            if (shouldShowCertificate && course.type === 'WORKSHOP') {
                const { programClass } = courseStudentView;
                const workshop: Workshop | undefined = programClass?.workshops?.find((workshop: Workshop) => course.id === workshop.course.id);
                shouldShowCertificate = !workshop?.nextMeeting;
            }

            setCanItShowCertificate(shouldShowCertificate);
        }
    }, [courseEnrollment, currentProgram, courseStudentView.programClass]);

    useEffect(() => {
        dispatch(
            getCourseStudentViewRequest({
                personId: person.id,
                courseId,
                moduleId: Constants.LMS_MODULE_ID,
                classEnrollmentId: enrollmentId,
            } as CourseRequest),
        );

        dispatch(getFavoriteListRequest());
        dispatch(increaseCourseViewsRequest(courseId));

        return () => {
            dispatch(clearCourse());
        };
    }, []);

    useEffect(() => {
        if (courseEnrollment && !courseEnrollment.id) {
            dispatch(
                startEnrollmentRequest({
                    personId: person.id,
                    courseId,
                    courseStartData: { classEnrollmentId: enrollmentId },
                } as StartEnrollmentRequest),
            );
        }
    }, [courseEnrollment]);

    const getWorkload = useCallback(() => {
        if (course.type === 'WORKSHOP' && courseStudentView.programClass.workshops) {
            const { workshops } = courseStudentView.programClass;
            const workshop = getWorkshopByCourseId(courseId, workshops);
            return (course.workload || 0) + ((workshop && workshop.workload) || 0);
        }

        return course.workload || 0;
    }, [course?.workload, courseStudentView?.programClass?.workshops]);

    return (
        <>
            {loading && <Loader />}
            {!loading && course && classEnrollment && (
                <div className="lms-course-page">
                    <GoBackMenu />

                    <div className="list-bg" id={Constants.TOP_IMG_ID}>
                        <img className="list-bg__img" src={course.mainImage} />
                        <div className="container list-bg__container">
                            <div className="list-bg__corner col-sm-10"></div>
                        </div>
                    </div>
                    <div className="container">
                        <div className="row">
                            <div className="col-sm-8 offset-sm-2 list-page__adjust">
                                <div className="list-content">
                                    <h1 className="list-content__title">{course.name}</h1>
                                    <div className="list-content__info">
                                        <div className="list-content__status">
                                            <span>
                                                {course.author}
                                                <i className="material-icons align-middle separator">{Constants.icons.CIRCLE}</i>
                                                {classEnrollment &&
                                                    lmsCourseService.getCourseStatus(courseEnrollment, classEnrollment.programClass, strings)}
                                            </span>

                                            <span>{`ID ${strings.CLASS}: ${programClassId || ' - ' + strings.UNTIL + ' ' + endDate}`}</span>
                                        </div>
                                    </div>

                                    <HorizontalMenu
                                        entity={{ id: course.id as number, type: EntityType.COURSE, name: course.name }}
                                        hasFavorite={classEnrollment.programClass.program.isCourseProgram && course.ownerId !== person.id}
                                        moduleId={Constants.LMS_MODULE_ID}
                                    />

                                    {course.description && <ReadMore value={course.description} />}
                                    <div className="list-content__duration">
                                        {course.type === 'WORKSHOP' && course.lessons?.length === 0
                                            ? '- '
                                            : `${course.lessons ? course.lessons.length : 0} `}
                                        {`${course.lessons && course.lessons.length === 1 ? strings.LESSON : strings.LESSONS}`}

                                        <i className="material-icons align-middle separator">{Constants.icons.CIRCLE}</i>
                                        {courseService.formatWorkload(getWorkload(), strings)}
                                        {currentProgram.hasCourseCertification && (
                                            <>
                                                <i className="material-icons align-middle separator">{Constants.icons.CIRCLE}</i>
                                                {courseCertificateMessage}
                                            </>
                                        )}
                                    </div>

                                    <WorkshopSlider workshop={getWorkshopByCourseId(courseId, courseStudentView.programClass.workshops)} />

                                    <div className="list-content__items" style={{ marginBottom: itemsMargin }}>
                                        {course.lessons &&
                                            course.lessons.map((lesson: Lesson, index) => {
                                                const status: string = lmsCourseService.getLessonStatus(courseEnrollment, lesson, strings);
                                                const lessonEnrollmentList: LessonEnrollment[] =
                                                    (courseEnrollment && courseEnrollment.lessonEnrollments) || [];

                                                const lessonEnrollment: LessonEnrollment = (courseService.getLessonEnrollmnetByLessonId(
                                                    lessonEnrollmentList,
                                                    lesson.id,
                                                ) as LessonEnrollment) || { lesson: { ...lesson }, status };

                                                const className: string = getClassNameByStatus(status, lessonEnrollment.prerequisitesMet);
                                                const blocked: boolean = className === 'disable-link';

                                                return (
                                                    <Link className={className} key={index} to={blocked ? '#' : linkRouteLesson(lesson)}>
                                                        <LMSContentCard
                                                            lessonEnrollment={lessonEnrollment}
                                                            lessonStatus={status}
                                                            setCanShowContentLink={setCanShowContentLink}
                                                        />
                                                    </Link>
                                                );
                                            })}
                                    </div>
                                </div>

                                {canItShowCertificate && (
                                    <DownloadCertificate
                                        loading={loadingCertificate}
                                        onClick={() => onClickDownloadCertificate()}
                                        title={strings.COMPLETED_COURSE_MESSAGE}
                                    />
                                )}
                            </div>
                            <SideMenu entityType={EntityType.COURSE} isMenuFixed rating />
                        </div>
                    </div>
                    <StudentRating
                        analyticEvent={LMSEvents.RATE_COURSE}
                        classification
                        entityId={course.id as number}
                        entityName={course.name}
                        entityType={Constants.entityTypes.COURSE}
                        title={strings.LABEL_RATING_LMS_COURSE}
                    />
                </div>
            )}
        </>
    );
};

export default LMSCoursePage;
